import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, doc, getDoc, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { liveGameDocs } from '../../helpers/games';
import { GameStatus } from '../../constants/gameConstants';

const getEventIdFromArg = (arg) => (typeof arg === 'string' ? arg : arg?.eventId);
const getForceFromArg = (arg) => (typeof arg === 'object' ? !!arg?.force : false);
const getGreenFromArg = (arg) => {
  if (typeof arg !== 'object') {
    return null;
  }

  const green = arg?.green;
  if (typeof green !== 'string') {
    return null;
  }

  const trimmedGreen = green.trim();
  return trimmedGreen ? trimmedGreen.toUpperCase() : null;
};

const getRequestKey = (eventId, green) => `${eventId}::${green || 'all'}`;
const getRequestKeyFromArg = (arg) => {
  const eventId = getEventIdFromArg(arg);
  if (!eventId) {
    return null;
  }

  return getRequestKey(eventId, getGreenFromArg(arg));
};

const initialState = {
  filtersByEventId: {},
  gamesByEventId: {},
  eventNamesById: {},
  sectionNamesByEventId: {},
  loadingByEventId: {},
  errorByEventId: {}
};

export const fetchPublicScoringGames = createAsyncThunk(
  'publicScoring/fetchPublicScoringGames',
  async (arg, thunkAPI) => {
    const eventId = getEventIdFromArg(arg);
    const green = getGreenFromArg(arg);
    if (!eventId) {
      return thunkAPI.rejectWithValue('Missing event ID');
    }

    const requestKey = getRequestKey(eventId, green);

    try {
      const gamesRef = collection(store, 'games');
      const oneHourAgo = Timestamp.fromMillis(Date.now() - 60 * 60 * 1000);

      const activeQueryConstraints = [
        where('event', '==', eventId),
        where('status', 'in', [GameStatus.NOT_STARTED, GameStatus.IN_PROGRESS, GameStatus.PLAYOFF])
      ];

      const recentCompletedQueryConstraints = [
        where('event', '==', eventId),
        where('status', '==', GameStatus.COMPLETE),
        where('updatedAt', '>=', oneHourAgo)
      ];

      if (green) {
        activeQueryConstraints.push(where('green', '==', green));
        recentCompletedQueryConstraints.push(where('green', '==', green));
      }

      const activeGamesQuery = query(gamesRef, ...activeQueryConstraints);
      const recentCompletedGamesQuery = query(gamesRef, ...recentCompletedQueryConstraints);

      const eventDocPromise = getDoc(doc(store, 'events', eventId));

      const [eventDoc, activeSnapshot, recentCompletedSnapshot] = await Promise.all([
        eventDocPromise,
        getDocs(activeGamesQuery),
        getDocs(recentCompletedGamesQuery)
      ]);

      const eventName = eventDoc.exists() ? eventDoc.data().name || 'Event' : 'Event';
      const eventSections = eventDoc.exists() ? eventDoc.data().sections || [] : [];

      const sectionNames = eventSections.reduce((acc, section) => {
        if (section?.id) {
          acc[section.id] = section.name || section.sectionName || null;
        }
        return acc;
      }, {});

      const gamesMap = new Map();

      liveGameDocs(activeSnapshot).forEach((docSnap) => {
        gamesMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
      });

      liveGameDocs(recentCompletedSnapshot).forEach((docSnap) => {
        gamesMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
      });

      const gamesData = Array.from(gamesMap.values());

      return { eventId, requestKey, eventName, games: gamesData, sectionNames };
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || 'Failed to load games');
    }
  },
  {
    condition: (arg, { getState }) => {
      const eventId = getEventIdFromArg(arg);
      if (!eventId) {
        return false;
      }

      const force = getForceFromArg(arg);
      if (force) {
        return true;
      }

      const state = getState();
      const requestKey = getRequestKeyFromArg(arg);
      const existingGames = state.publicScoring?.gamesByEventId?.[requestKey];
      return !Array.isArray(existingGames) || existingGames.length === 0;
    }
  }
);

const publicScoringSlice = createSlice({
  name: 'publicScoring',
  initialState,
  reducers: {
    setRoundFilter: (state, action) => {
      const { eventId, round } = action.payload;
      if (!state.filtersByEventId[eventId]) {
        state.filtersByEventId[eventId] = { round: null, greenRink: null };
      }
      state.filtersByEventId[eventId].round = round;
    },
    setGreenRinkFilter: (state, action) => {
      const { eventId, greenRink } = action.payload;
      if (!state.filtersByEventId[eventId]) {
        state.filtersByEventId[eventId] = { round: null, greenRink: null };
      }
      state.filtersByEventId[eventId].greenRink = greenRink;
    },
    clearFilters: (state, action) => {
      const { eventId } = action.payload;
      if (state.filtersByEventId[eventId]) {
        state.filtersByEventId[eventId] = { round: null, greenRink: null };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicScoringGames.pending, (state, action) => {
        const requestKey = getRequestKeyFromArg(action.meta.arg);
        if (!requestKey) {
          return;
        }

        // A forced refresh after scoring keeps the already-loaded list on
        // screen; only the first load has nothing else to show.
        const cachedGames = state.gamesByEventId[requestKey];
        const hasCachedGames = Array.isArray(cachedGames) && cachedGames.length > 0;
        state.loadingByEventId[requestKey] = !hasCachedGames;
        state.errorByEventId[requestKey] = null;
      })
      .addCase(fetchPublicScoringGames.fulfilled, (state, action) => {
        const { eventId, requestKey, eventName, games, sectionNames } = action.payload;
        state.gamesByEventId[requestKey] = games;
        state.eventNamesById[eventId] = eventName;
        state.sectionNamesByEventId[eventId] = sectionNames;
        state.loadingByEventId[requestKey] = false;
        state.errorByEventId[requestKey] = null;
      })
      .addCase(fetchPublicScoringGames.rejected, (state, action) => {
        const requestKey = getRequestKeyFromArg(action.meta.arg);
        if (!requestKey) {
          return;
        }

        const cachedGames = state.gamesByEventId[requestKey];
        const hasCachedGames = Array.isArray(cachedGames) && cachedGames.length > 0;
        state.loadingByEventId[requestKey] = false;
        // Keep showing the cached list if a background refresh fails.
        state.errorByEventId[requestKey] = hasCachedGames
          ? null
          : action.payload || 'Failed to load games';
      });
  }
});

export const { setRoundFilter, setGreenRinkFilter, clearFilters } = publicScoringSlice.actions;
export default publicScoringSlice.reducer;
